import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type CoverageFlatTeamSettingGroupId =
  | "coverage.custom_logo"
  | "coverage.info_modal"
  | "coverage.general_coverage"
  | "coverage.exclusions"
  | "coverage.return_coverage"
  | "coverage.checkout_ui_customizations"
  | "coverage.display_options"
  | "coverage.package_protection"
  | "coverage.pre_protected_orders"
  | "coverage.advanced_coverage"
  | "coverage.digital_warehouse";

const customLogoOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.shopifyExtension",
  product: "returns",
};

const infoModalOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.infoModal",
  product: "returns",
};

const settingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings",
  product: "returns",
};

const shopifyExtensionOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.shopifyExtension",
  product: "returns",
};

const checkoutUICustomizationsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.shopifyExtension.checkoutUICustomizations",
  product: "returns",
};

const packageProtectionOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.packageProtection",
  product: "returns",
};

const customerFacingRisk: SettingRisk = {
  level: "medium",
  reason:
    "Changes affect the customer-facing return coverage widget and modal shoppers see at checkout.",
};

const digitalWarehouseOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.shopifyExtension.digitalWarehouse",
  product: "returns",
};

const coverageEligibilityRisk: SettingRisk = {
  level: "high",
  reason:
    "Changes affect which orders receive return coverage and what shoppers are charged for it.",
};

const coverageParentPageTarget = {
  type: "settings",
  path: "coverage",
} as const;

const coverageBuilderPageTarget = {
  type: "settings",
  path: "coverage/coverage-builder",
} as const;

export const CoverageFlatTeamSettingGroups = [
  {
    id: "coverage.custom_logo",
    title: "Coverage Custom Logo",
    description:
      "Branding shown next to the return coverage toggle: the custom logo image, the optional badge icon, its colors, and the cart product image.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: customLogoOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "coverage/custom-logo" },
    synonyms: [
      "custom logo",
      "coverage logo",
      "cart logo",
      "toggle icon",
      "coverage badge",
      "widget logo",
      "brand logo",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing branding shown next to the coverage toggle in the customer cart.",
    },
  },
  {
    id: "coverage.info_modal",
    title: "Coverage Info Modal",
    description:
      "The 'learn more' modal shoppers open from the return coverage toggle: header and body copy, imagery, cart/checkout entry points, and custom styling.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: infoModalOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "coverage/info-modal" },
    synonyms: [
      "info modal",
      "coverage modal",
      "learn more modal",
      "info popup",
      "return coverage modal",
      "modal customization",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing the customer-facing coverage info modal copy or imagery.",
    },
  },
  {
    id: "coverage.general_coverage",
    title: "Coverage Availability",
    description:
      "Where return coverage is offered to shoppers: showing the coverage toggle on the product detail page and excluding in-store pickup orders from coverage.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "coverage availability",
      "coverage eligibility",
      "show on product page",
      "product page coverage",
      "exclude in-store pickup",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing where return coverage is offered to shoppers.",
    },
  },
  {
    id: "coverage.exclusions",
    title: "Coverage Exclusions",
    description:
      "Rules that exclude orders, sources, customers, properties, metafields, and discount codes from being offered return coverage.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "coverage exclusions",
      "exclude order tags",
      "exclude sources",
      "exclude customer tags",
      "exclude properties",
      "exclude variant metafields",
      "exclude discount codes",
      "require exact match",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing which orders and products are excluded from coverage.",
    },
  },
  {
    id: "coverage.return_coverage",
    title: "Return Coverage",
    description:
      "The return coverage offering: which return methods are covered, final-sale coverage, and per-item exclusions such as product tags, collections, and SKUs.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "return coverage",
      "covered return methods",
      "final sale coverage",
      "exclude product tags",
      "exclude collections",
      "exclude skus",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing which return methods are covered or which items are excluded.",
    },
  },
  {
    id: "coverage.checkout_ui_customizations",
    title: "Checkout UI Customizations",
    description:
      "Appearance of the Redo coverage element in Shopify checkout: title and subtext sizes and the spacing around the element.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: checkoutUICustomizationsOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageBuilderPageTarget,
    synonyms: [
      "checkout ui customizations",
      "title text size",
      "subtext size",
      "checkout spacing",
      "checkout coverage appearance",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing the coverage element's checkout appearance.",
    },
  },
  {
    id: "coverage.display_options",
    title: "Coverage Display Options",
    description:
      "Appearance of coverage components on your storefront: info text, font size, and the CSS selector used to place the coverage element on the product page.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "display options",
      "coverage info text",
      "coverage font size",
      "coverage css selector",
      "coverage appearance",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing the coverage components' storefront appearance.",
    },
  },
  {
    id: "coverage.package_protection",
    title: "Package Protection Pricing",
    description:
      "What shoppers pay for package protection at checkout: the pricing strategy, the price floor and ceiling, the cart totals it is offered between, and the domestic and international pricing tiers.",
    productArea: "returns",
    access: {
      read: {
        minVisibility: "merchant",
        permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      },
      write: {
        mode: "none",
        reason:
          "Admin-only under Package Protection Plus, and the bounds only run on the coverage page's save. The consolidated tab reports these values without editing them.",
      },
    },
    owner: packageProtectionOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "never" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "package protection pricing",
      "package protection price",
      "shipping protection price",
      "protection pricing tiers",
      "package protection min price",
      "package protection max price",
    ],
    changeGuidance: {
      summary:
        "Changing these changes what every shopper is charged for package protection — confirm the tiers before saving.",
    },
  },
  {
    id: "coverage.pre_protected_orders",
    title: "Pre-Protected Orders",
    description:
      "Automatically add return coverage to eligible orders that were placed without it.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: settingsOwner,
    risk: coverageEligibilityRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "pre-protected orders",
      "pre protected orders",
      "retroactive coverage",
      "auto add coverage",
    ],
    changeGuidance: {
      summary:
        "Confirm before enabling automatic coverage on orders placed without it.",
    },
  },
  {
    id: "coverage.advanced_coverage",
    title: "Advanced Coverage",
    description:
      "Advanced Shopify coverage behavior, including whether Redo removes the cart properties it adds during checkout after each order is created.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: shopifyExtensionOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: [
      "advanced coverage",
      "delete cart properties",
      "cart properties",
      "shopify coverage advanced",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing advanced Shopify coverage cart behavior.",
    },
  },
  {
    id: "coverage.digital_warehouse",
    title: "Redo Digital Warehouse",
    description:
      "Keeps the Redo coverage product in stock through its own Redo digital warehouse location in Shopify, so it never uses up inventory at your locations.",
    productArea: "returns",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: digitalWarehouseOwner,
    risk: customerFacingRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: coverageParentPageTarget,
    synonyms: ["digital warehouse", "redo warehouse"],
    changeGuidance: {
      summary:
        "Confirm before turning this on or off, since it changes where Shopify tracks stock for the Redo coverage product.",
    },
  },
] as const satisfies readonly SettingGroupDefinition<CoverageFlatTeamSettingGroupId>[];

type _assertAllCoverageFlatTeamSettingGroupIdsProvided =
  CoverageFlatTeamSettingGroupId extends (typeof CoverageFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allCoverageFlatTeamSettingGroupIdsProvided: _assertAllCoverageFlatTeamSettingGroupIdsProvided = true;
void _allCoverageFlatTeamSettingGroupIdsProvided;

export const CoverageFlatTeamSettingGroupById: Record<
  CoverageFlatTeamSettingGroupId,
  SettingGroupDefinition<CoverageFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      CoverageFlatTeamSettingGroupId,
      SettingGroupDefinition<CoverageFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of CoverageFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    CoverageFlatTeamSettingGroupId,
    SettingGroupDefinition<CoverageFlatTeamSettingGroupId>
  >;
})();
